import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import { AccessTime as AccessTimeIcon, ChatBubbleOutline as ChatBubbleIcon } from '@mui/icons-material';
import { ChatMessageDownloadButton, ExternalLinkButton } from '../common';
import {
  Nurse,
  NURSE_TYPE_THEME,
  WORKSHOP_ROOM_STATUS_TYPES,
  WORKSHOP_STATUS_COLORS,
  WORKSHOP_STATUS_TEXT_COLORS,
  WORKSHOP_STATUS_TYPES
} from '../../types/nurse';
import { formatDateWithTime, getHumanReadableTimeAgo } from '../../utils/dateUtils';
import { getExternalWorkshopRoomUrl } from '../../utils/urlUtils';
import { MESSAGE_DISPLAY_LIMIT } from '../../types/common';
// 研修ステータスのラベルを取得する関数
const getWorkshopStatusLabel = (status: string): string => {
  return WORKSHOP_STATUS_TYPES[status as keyof typeof WORKSHOP_STATUS_TYPES] || status;
};

interface WorkshopSectionProps {
  nurse: Nurse;
}

const WorkshopSection: React.FC<WorkshopSectionProps> = ({ nurse }) => {
  return (
    <Card
      sx={{
        mb: 4,
        border: `1px solid ${NURSE_TYPE_THEME.TRAINING.BORDER}40`,
        boxShadow: `0 1px 4px ${NURSE_TYPE_THEME.TRAINING.BACKGROUND}30`
      }}>
      <CardContent>
        <Grid container>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6">研修</Typography>
              {nurse.role_type === 'STAFF' && (
                <Box
                  sx={{
                    display: 'inline-block',
                    border: () => {
                      const color = WORKSHOP_STATUS_COLORS[nurse.workshop_status as keyof typeof WORKSHOP_STATUS_COLORS] || '#bdbdbd';
                      return `1px solid ${color}`;
                    },
                    px: 1.5,
                    py: 0.25,
                    fontSize: '0.8rem',
                    ml: 2,
                    textAlign: 'center' // 中央揃え
                  }}
                >
                  <Typography
                    component="span"
                    sx={{
                      fontWeight: 'medium',
                      color: () => WORKSHOP_STATUS_TEXT_COLORS[nurse.workshop_status as keyof typeof WORKSHOP_STATUS_TEXT_COLORS] || '#757575',
                    }}
                  >
                    {getWorkshopStatusLabel(nurse.workshop_status)}
                  </Typography>
                </Box>
              )}
            </Box>
            <Divider sx={{ mb: 2 }}/>

            {!nurse.workshop_rooms || nurse.workshop_rooms.length === 0 ? (
              <Box sx={{ p: 2 }}>
                <Typography
                  variant="body1"
                  sx={{ color: 'text.disabled', fontStyle: 'italic' }}
                >
                  研修ルームはありません
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ mt: 2, borderRadius: 1 }}>
                <Table aria-label="研修ルーム一覧">
                  <TableHead
                    sx={{ backgroundColor: NURSE_TYPE_THEME.TRAINING.BACKGROUND, borderBottom: `2px solid ${NURSE_TYPE_THEME.TRAINING.BORDER}` }}>
                    <TableRow>
                      <TableCell width="10%" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                        ステータス
                      </TableCell>
                      <TableCell width="25%" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                        アシスタント
                      </TableCell>
                      <TableCell width="10%" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                        研修回数
                      </TableCell>
                      <TableCell width="15%" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                        送信回数
                      </TableCell>
                      <TableCell width="27%" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                        最終メッセージ
                      </TableCell>
                      <TableCell width="13%" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                        チャット
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {nurse.workshop_rooms && nurse.workshop_rooms.length > 0 && [ ...nurse.workshop_rooms ]
                    .sort((a, b) => b.workshop_count - a.workshop_count)
                    .map((room) => (
                      <TableRow
                        key={room.id}
                        hover
                        sx={{
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                          }
                        }}
                      >
                        {/* ステータス */}
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              color: WORKSHOP_STATUS_TEXT_COLORS[room.workshop_room_status as keyof typeof WORKSHOP_STATUS_TEXT_COLORS] || '#757575',
                              fontSize: '0.75rem',
                              borderLeft: `3px solid ${WORKSHOP_STATUS_COLORS[room.workshop_room_status as keyof typeof WORKSHOP_STATUS_COLORS] || '#bdbdbd'}`,
                              pl: 1,
                              py: 0.3,
                              opacity: 0.85,
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {WORKSHOP_ROOM_STATUS_TYPES[room.workshop_room_status as keyof typeof WORKSHOP_ROOM_STATUS_TYPES] || room.workshop_room_status}
                          </Typography>
                        </TableCell>

                        {/* アシスタント名 */}
                        <TableCell>
                          <Typography sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                            {room.child_assistant?.name || `アシスタントID: ${room.child_id}`}
                          </Typography>
                        </TableCell>

                        {/* 研修回数 */}
                        <TableCell>
                          <Typography sx={{ fontWeight: 'bold', fontSize: '1rem', textAlign: 'center' }}>
                            {room.workshop_count}回
                          </Typography>
                        </TableCell>

                        {/* 送信回数 */}
                        <TableCell>
                          <Typography sx={{ fontSize: '0.9rem' }}>
                            {room.send_count}回 ({room.send_char_count.toLocaleString()}文字)
                          </Typography>
                        </TableCell>

                        {/* 最終メッセージと送信日時 */}
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            {/* 最終チャットメッセージ */}
                            {room.last_chat_message ? (
                              room.last_chat_message.length > MESSAGE_DISPLAY_LIMIT ? (
                                // 制限文字数を超える場合はTooltipを表示
                                <Tooltip
                                  title={room.last_chat_message}
                                  arrow
                                  placement="top"
                                  enterDelay={500}
                                  sx={{ maxWidth: 500 }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: '0.85rem',
                                      color: 'text.primary',
                                      cursor: 'help',
                                      mb: 0.5
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                      {`${room.last_chat_message.substring(0, MESSAGE_DISPLAY_LIMIT)}...`}
                                      <ChatBubbleIcon sx={{ ml: 0.5, fontSize: '0.9rem', color: 'text.secondary', opacity: 0.7 }}/>
                                    </Box>
                                  </Typography>
                                </Tooltip>
                              ) : (
                                // 17文字以内の場合はTooltipなし
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: '0.85rem',
                                    color: 'text.primary',
                                    mb: 0.5
                                  }}
                                >
                                  {room.last_chat_message}
                                </Typography>
                              )
                            ) : (
                              // メッセージがない場合
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: '0.85rem',
                                  color: 'text.secondary',
                                  fontStyle: 'italic',
                                  mb: 0.5
                                }}
                              >
                                メッセージはありません
                              </Typography>
                            )}

                            {room.last_chat_send_at && (
                              <Tooltip
                                title={`最終送信日時: ${formatDateWithTime(room.last_chat_send_at)}`}
                                arrow
                                placement="left"
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'help', mt: 0.5 }}>
                                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    最終送信: {getHumanReadableTimeAgo(room.last_chat_send_at)}
                                  </Typography>
                                  <AccessTimeIcon fontSize="small" color="action" sx={{ fontSize: '0.8rem', opacity: 0.7 }}/>
                                </Box>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>

                        {/* チャットリンク */}
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1.5 }}>
                            <ExternalLinkButton
                              href={getExternalWorkshopRoomUrl(room.id)}
                              label="開く"
                              tooltip="研修ルームを開く"
                              tooltipPlacement="left"
                              size="small"
                              variant="outlined"
                              buttonType="chat"
                              themeColor={{
                                main: NURSE_TYPE_THEME.TRAINING.DARK,
                                hover: `${NURSE_TYPE_THEME.TRAINING.BACKGROUND}99`
                              }}
                              sx={{ minWidth: 'auto', px: 1 }}
                            />
                            <div onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}>
                              <ChatMessageDownloadButton
                                roomType="WORKSHOP_ROOM"
                                roomId={room.id}
                                size="small"
                                variant="outlined"
                                iconOnly={true}
                                themeColor={{
                                  main: NURSE_TYPE_THEME.TRAINING.DARK,
                                  hover: NURSE_TYPE_THEME.TRAINING.BACKGROUND
                                }}
                              />
                            </div>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};
export default WorkshopSection;
